import React, { useState, useEffect } from 'react';
import { X, Crown, Globe, Lock, Trash2, Edit2, User, Image, Calendar } from 'lucide-react';
import './AdminAlbumManager.css';
import { getApiUrl } from '../utils/config';
import authService from '../services/authService';

const AdminAlbumManager = ({ onClose }) => {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadAllAlbums();
  }, []);

  const loadAllAlbums = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await authService.authenticatedFetch(`${getApiUrl()}/api/albums/admin/all`);
      
      if (response.ok) {
        const data = await response.json();
        setAlbums(data.albums || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load albums');
      }
    } catch (err) {
      setError('Network error loading albums');
      console.error('Error loading admin albums:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAlbum = (album) => {
    setEditingAlbum({
      ...album,
      newName: album.name,
      newDescription: album.description || ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editingAlbum) return;

    try {
      const response = await authService.authenticatedFetch(
        `${getApiUrl()}/api/albums/admin/${editingAlbum.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: editingAlbum.newName,
            description: editingAlbum.newDescription
          }),
        }
      );

      if (response.ok) {
        const updatedAlbum = await response.json();
        setAlbums(albums.map(album => 
          album.id === editingAlbum.id 
            ? { ...album, name: updatedAlbum.name, description: updatedAlbum.description }
            : album
        ));
        setEditingAlbum(null);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to update album');
      }
    } catch (error) {
      console.error('Error updating album:', error);
      alert('Error updating album');
    }
  };

  const handleTogglePublic = async (album) => {
    try {
      const response = await authService.authenticatedFetch(
        `${getApiUrl()}/api/albums/admin/${album.id}/toggle-public`,
        {
          method: 'PUT'
        }
      );

      if (response.ok) {
        const updatedAlbum = await response.json();
        setAlbums(albums.map(a => 
          a.id === album.id ? { ...a, is_public: updatedAlbum.is_public } : a
        ));
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to toggle album privacy');
      }
    } catch (error) {
      console.error('Error toggling album privacy:', error);
      alert('Error toggling album privacy');
    }
  };

  const handleDeleteClick = (album) => {
    setDeleteConfirm(album);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;

    setDeleting(true);
    try {
      const response = await authService.authenticatedFetch(
        `${getApiUrl()}/api/albums/admin/${deleteConfirm.id}`,
        {
          method: 'DELETE'
        }
      );

      if (response.ok) {
        setAlbums(albums.filter(a => a.id !== deleteConfirm.id));
        setDeleteConfirm(null);
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to delete album');
      }
    } catch (error) {
      console.error('Error deleting album:', error);
      alert('Error deleting album');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAlbumThumbnail = (album) => {
    if (album.cover_photo_id) {
      return `${getApiUrl()}/api/albums/${album.id}/thumbnail`;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="admin-modal-overlay">
        <div className="admin-modal">
          <div className="admin-modal-header">
            <h2><Crown size={24} /> Admin - Album Management</h2>
            <button className="close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>
          <div className="admin-loading">
            <p>Loading albums...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="admin-modal-overlay">
        <div className="admin-modal">
          <div className="admin-modal-header">
            <h2><Crown size={24} /> Admin - Album Management</h2>
            <button className="close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>

          {error && (
            <div className="admin-error">
              <p>{error}</p>
              <button onClick={loadAllAlbums}>Retry</button>
            </div>
          )}

          <div className="admin-content">
            {albums.length === 0 ? (
              <div className="admin-empty">
                <p>No albums found</p>
              </div>
            ) : (
              <div className="admin-albums-list">
                {albums.map(album => (
                  <div key={album.id} className="admin-album-item">
                    <div className="admin-album-thumbnail">
                      {getAlbumThumbnail(album) ? (
                        <img 
                          src={getAlbumThumbnail(album)} 
                          alt={album.name}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className="admin-album-placeholder" 
                        style={{display: getAlbumThumbnail(album) ? 'none' : 'flex'}}
                      >
                        <Image size={32} />
                      </div>
                    </div>

                    <div className="admin-album-info">
                      {editingAlbum && editingAlbum.id === album.id ? (
                        <div className="admin-album-edit">
                          <input
                            type="text"
                            value={editingAlbum.newName}
                            onChange={(e) => setEditingAlbum({
                              ...editingAlbum,
                              newName: e.target.value
                            })}
                            placeholder="Album name"
                          />
                          <textarea
                            value={editingAlbum.newDescription}
                            onChange={(e) => setEditingAlbum({
                              ...editingAlbum,
                              newDescription: e.target.value
                            })}
                            placeholder="Album description"
                            rows="2"
                          />
                          <div className="admin-edit-actions">
                            <button onClick={handleSaveEdit} className="btn-save">Save</button>
                            <button onClick={() => setEditingAlbum(null)} className="btn-cancel">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="admin-album-details">
                          <h3>{album.name}</h3>
                          {album.description && <p className="album-description">{album.description}</p>}
                          <div className="admin-album-meta">
                            <span className="album-owner">
                              <User size={14} />
                              {album.owner_username || 'Unknown'}
                            </span>
                            <span className="album-photos">
                              <Image size={14} />
                              {album.photo_count || 0} photos
                            </span>
                            <span className="album-date">
                              <Calendar size={14} />
                              {formatDate(album.created_at)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="admin-album-status">
                      <span className={`privacy-badge ${album.is_public ? 'public' : 'private'}`}>
                        {album.is_public ? <Globe size={16} /> : <Lock size={16} />}
                        {album.is_public ? 'Public' : 'Private'}
                      </span>
                    </div>

                    <div className="admin-album-actions">
                      <button
                        onClick={() => handleEditAlbum(album)}
                        className="admin-action-btn edit-btn"
                        title="Edit album"
                        disabled={editingAlbum !== null}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleTogglePublic(album)}
                        className="admin-action-btn toggle-btn"
                        title={`Make ${album.is_public ? 'private' : 'public'}`}
                        disabled={editingAlbum !== null}
                      >
                        {album.is_public ? <Lock size={16} /> : <Globe size={16} />}
                      </button>
                      <button
                        onClick={() => handleDeleteClick(album)}
                        className="admin-action-btn delete-btn"
                        title="Delete album"
                        disabled={editingAlbum !== null}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="delete-confirm-overlay">
          <div className="delete-confirm-modal">
            <h3>Delete Album</h3>
            <p>Are you sure you want to delete "{deleteConfirm.name}"?</p>
            <p className="delete-warning">
              This will permanently delete the album and all {deleteConfirm.photo_count || 0} photos. 
              This action cannot be undone.
            </p>
            <div className="delete-confirm-actions">
              <button 
                className="btn-cancel" 
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="btn-delete" 
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Album'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminAlbumManager;